import api from './api';
import { CategoryResponse, ModelResponse, PartGroupResponse, SparePartResponse, CompatibleModelsResponse, CartResponse, AddToCartResponse, Category, Model, PartGroup } from '../types/SpareParts';

/**
 * Ana kategorileri getirir
 * @returns Kategori listesi
 */
export const getCategories = async (): Promise<CategoryResponse> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'true');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'KategoriModelGrup');
  formData.append('fields', 'id,parentId,index,depth,expanded,expandable,checked,cls,iconCls,icon,root,isLast,isFirst,allowDrop,allowDrag,loaded,loading,href,hrefTarget,qtip,qtitle,qshowDelay,children,name,guid');
  formData.append('writemodel', '');
  formData.append('fromview', 'false');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('sort', 'Ad');
  formData.append('dir', 'ASC');
  formData.append('node', 'root');

  try {
    const response = await api.post<CategoryResponse>('/tree', formData);
    return response.data;
  } catch (error) {
    console.error('Kategori listesi alma hatası:', error);
    throw error;
  }
};

/**
 * Seçili kategoriye ait modelleri getirir
 * @param category Seçili kategori
 * @returns Model listesi
 */
export const getModels = async (category: Category): Promise<ModelResponse> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'true');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'KategoriModelGrup');
  formData.append('fields', 'id,parentId,index,depth,expanded,expandable,checked,cls,iconCls,icon,root,isLast,isFirst,allowDrop,allowDrag,loaded,loading,href,hrefTarget,qtip,qtitle,qshowDelay,children,name,guid');
  formData.append('writemodel', '');
  formData.append('fromview', 'false');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('parent', category.self.toString());
  formData.append('depth', '1');
  formData.append('sort', 'Ad');
  formData.append('dir', 'ASC');
  formData.append('node', category.guid);

  try {
    const response = await api.post<ModelResponse>('/tree', formData);
    return response.data;
  } catch (error) {
    console.error('Model listesi alma hatası:', error);
    throw error;
  }
};

/**
 * Seçili modele ait parça gruplarını getirir
 * @param category Seçili kategori
 * @param model Seçili model
 * @returns Parça grubu listesi
 */
export const getPartGroups = async (category: Category, model: Model): Promise<PartGroupResponse> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'true');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'KategoriModelGrup');
  formData.append('fields', 'id,parentId,index,depth,expanded,expandable,checked,cls,iconCls,icon,root,isLast,isFirst,allowDrop,allowDrag,loaded,loading,href,hrefTarget,qtip,qtitle,qshowDelay,children,name,guid');
  formData.append('writemodel', '');
  formData.append('fromview', 'false');
  formData.append('masterfield', '');
  formData.append('masterid', category.self.toString());
  formData.append('parent', model.self.toString());
  formData.append('depth', '2');
  formData.append('sort', 'Ad');
  formData.append('dir', 'ASC');
  formData.append('node', model.guid);

  try {
    const response = await api.post<PartGroupResponse>('/tree', formData);
    return response.data;
  } catch (error) {
    console.error('Parça grubu listesi alma hatası:', error);
    throw error;
  }
};

/**
 * Seçili kategoriye ait yedek parçaları getirir
 * @param category Seçili kategori
 * @param page Sayfa numarası
 * @param limit Sayfa başına kayıt sayısı
 * @param hideOutOfStock Stokta olmayanları gizle
 * @returns Yedek parça listesi
 */
export const getSparePartsByCategory = async (
  category: Category, 
  page: number = 1, 
  limit: number = 50,
  hideOutOfStock: boolean = false
): Promise<SparePartResponse> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'YedekParcalar');
  formData.append('fields', 'Kod,Ad,Gorsel,GrupAdi,Stok,CariStokDurumu,ListeFiyati,BayiFiyati,IscilikBedeli,PromosyonluMu,AktifMi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('prefilter', '[{"field":"AktifMi","type":"boolean","value":true}]');
  formData.append('modelparams', category.self.toString());
  formData.append('modelparams', '0');
  formData.append('modelparams', '0');
  
  // Stokta olmayanları gizle filtresi
  if (hideOutOfStock) {
    formData.append('filter', '[{"type":"boolean","value":true,"field":"CariStokDurumu"}]');
  }
  
  formData.append('page', page.toString());
  formData.append('start', ((page - 1) * limit).toString());
  formData.append('limit', limit.toString());
  formData.append('sort', 'Kod');
  formData.append('dir', 'ASC');

  try {
    const response = await api.post<SparePartResponse>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Kategori yedek parça listesi alma hatası:', error);
    throw error;
  }
};

/**
 * Seçili modele ait yedek parçaları getirir
 * @param model Seçili model
 * @param page Sayfa numarası
 * @param limit Sayfa başına kayıt sayısı
 * @param hideOutOfStock Stokta olmayanları gizle
 * @returns Yedek parça listesi
 */
export const getSparePartsByModel = async (
  model: Model, 
  page: number = 1, 
  limit: number = 50,
  hideOutOfStock: boolean = false
): Promise<SparePartResponse> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'YedekParcalar');
  formData.append('fields', 'Kod,Ad,Gorsel,GrupAdi,Stok,CariStokDurumu,ListeFiyati,BayiFiyati,IscilikBedeli,PromosyonluMu,AktifMi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('prefilter', '[{"field":"AktifMi","type":"boolean","value":true}]');
  formData.append('modelparams', '0');
  formData.append('modelparams', model.self.toString());
  formData.append('modelparams', '0');
  
  // Stokta olmayanları gizle filtresi
  if (hideOutOfStock) {
    formData.append('filter', '[{"type":"boolean","value":true,"field":"CariStokDurumu"}]');
  }
  
  formData.append('page', page.toString());
  formData.append('start', ((page - 1) * limit).toString());
  formData.append('limit', limit.toString());
  formData.append('sort', 'Kod');
  formData.append('dir', 'ASC');

  try {
    const response = await api.post<SparePartResponse>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Model yedek parça listesi alma hatası:', error);
    throw error;
  }
};

/**
 * Seçili parça grubuna ait yedek parçaları getirir
 * @param model Seçili model
 * @param partGroup Seçili parça grubu
 * @param page Sayfa numarası
 * @param limit Sayfa başına kayıt sayısı
 * @param hideOutOfStock Stokta olmayanları gizle
 * @returns Yedek parça listesi
 */
export const getSpareParts = async (
  model: Model, 
  partGroup: PartGroup, 
  page: number = 1, 
  limit: number = 50,
  hideOutOfStock: boolean = false
): Promise<SparePartResponse> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'YedekParcalar');
  formData.append('fields', 'Kod,Ad,Gorsel,GrupAdi,Stok,CariStokDurumu,ListeFiyati,BayiFiyati,IscilikBedeli,PromosyonluMu,AktifMi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('prefilter', '[{"field":"AktifMi","type":"boolean","value":true}]');
  formData.append('modelparams', '0');
  formData.append('modelparams', model.self.toString());
  formData.append('modelparams', partGroup.self.toString());
  
  // Stokta olmayanları gizle filtresi
  if (hideOutOfStock) {
    formData.append('filter', '[{"type":"boolean","value":true,"field":"CariStokDurumu"}]');
  }
  
  formData.append('page', page.toString());
  formData.append('start', ((page - 1) * limit).toString());
  formData.append('limit', limit.toString());
  formData.append('sort', 'Kod');
  formData.append('dir', 'ASC');

  try {
    const response = await api.post<SparePartResponse>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Yedek parça listesi alma hatası:', error);
    throw error;
  }
};

/**
 * Promosyonlu yedek parçaları getirir
 * @param page Sayfa numarası
 * @param limit Sayfa başına kayıt sayısı
 * @param hideOutOfStock Stokta olmayanları gizle
 * @returns Promosyonlu yedek parça listesi
 */
export const getPromotionalSpareParts = async (
  page: number = 1, 
  limit: number = 50,
  hideOutOfStock: boolean = false
): Promise<SparePartResponse> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'YedekParcalar');
  formData.append('fields', 'Kod,Ad,Gorsel,GrupAdi,Stok,CariStokDurumu,ListeFiyati,BayiFiyati,IscilikBedeli,PromosyonluMu,AktifMi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('prefilter', '[{"field":"AktifMi","type":"boolean","value":true}]');
  formData.append('modelparams', '0');
  formData.append('modelparams', '0');
  formData.append('modelparams', '0');
  
  // Promosyonlu ürünler filtresi
  let filters = '[{"type":"boolean","value":true,"field":"PromosyonluMu"}';
  
  // Stokta olmayanları gizle filtresi
  if (hideOutOfStock) {
    filters += ',{"type":"boolean","value":true,"field":"CariStokDurumu"}';
  }
  
  filters += ']';
  formData.append('filter', filters);
  
  formData.append('page', page.toString());
  formData.append('start', ((page - 1) * limit).toString());
  formData.append('limit', limit.toString());
  formData.append('sort', 'Kod');
  formData.append('dir', 'ASC');

  try {
    const response = await api.post<SparePartResponse>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Promosyonlu yedek parçalar alma hatası:', error);
    throw error;
  }
};

/**
 * Yedek parçanın uyumlu modellerini getirir
 * @param sparePartId Yedek parça ID'si
 * @returns Uyumlu model listesi
 */
export const getCompatibleModels = async (sparePartId: number): Promise<CompatibleModelsResponse> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'UyumluModeller');
  formData.append('fields', 'Ad,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'false');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('modelparams', sparePartId.toString());
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '50');

  try {
    const response = await api.post<CompatibleModelsResponse>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Uyumlu modeller alma hatası:', error);
    throw error;
  }
};

/**
 * Sepet içeriğini getirir
 * @returns Sepet içeriği
 */
export const getCartItems = async (): Promise<CartResponse> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'Sepet');
  formData.append('fields', 'Siparis,SiparisId,SiparisNo,YedekParca,Kod,Ad,GrupAdi,Gorsel,FiiliStok,Miktar,Fiyat,Indirim,TeminEdilen,Kalan,SiparisTutari,FaturaTutari,FiiliStokDurumu,PromosyonluMu,ModernArayuzdenMi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('prefilter', '[{"type":"int","field":"SiparisId","value":0}]');
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '50');
  formData.append('sort', 'Id');
  formData.append('dir', 'ASC');

  try {
    const response = await api.post<CartResponse>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Sepet içeriği alma hatası:', error);
    throw error;
  }
};

/**
 * Arama terimine göre yedek parçaları getirir
 * @param query Arama terimi
 * @param searchType Arama türü ('code' | 'description')
 * @param page Sayfa numarası
 * @param limit Sayfa başına kayıt sayısı
 * @param hideOutOfStock Stokta olmayanları gizle
 * @param category Seçili kategori (opsiyonel)
 * @param model Seçili model (opsiyonel)
 * @param partGroup Seçili parça grubu (opsiyonel)
 * @returns Yedek parça listesi
 */
export const searchSpareParts = async (
  query: string,
  searchType: 'code' | 'description',
  page: number = 1,
  limit: number = 50,
  hideOutOfStock: boolean = false,
  category?: Category,
  model?: Model,
  partGroup?: PartGroup
): Promise<SparePartResponse> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'YedekParcalar');
  formData.append('fields', 'Kod,Ad,Gorsel,GrupAdi,Stok,CariStokDurumu,ListeFiyati,BayiFiyati,IscilikBedeli,PromosyonluMu,AktifMi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('prefilter', '[{"field":"AktifMi","type":"boolean","value":true}]');
  
  // Model parametreleri - kategori, model, parça grubu filtresi
  if (category && model && partGroup) {
    formData.append('modelparams', category.self.toString());
    formData.append('modelparams', model.self.toString());
    formData.append('modelparams', partGroup.self.toString());
  } else if (category && model) {
    formData.append('modelparams', category.self.toString());
    formData.append('modelparams', model.self.toString());
    formData.append('modelparams', '0');
  } else if (category) {
    formData.append('modelparams', category.self.toString());
    formData.append('modelparams', '0');
    formData.append('modelparams', '0');
  } else {
    formData.append('modelparams', '0');
    formData.append('modelparams', '0');
    formData.append('modelparams', '0');
  }
  
  // Arama filtresi
  const searchFilters = [];
  
  // Arama türüne göre filtre
  if (searchType === 'code') {
    searchFilters.push({
      "type": "string",
      "value": query,
      "field": "Kod"
    });
  } else {
    searchFilters.push({
      "type": "string", 
      "value": query,
      "field": "Ad"
    });
  }
  
  // Stokta olmayanları gizle filtresi
  if (hideOutOfStock) {
    searchFilters.push({
      "type": "boolean",
      "value": true,
      "field": "CariStokDurumu"
    });
  }
  
  if (searchFilters.length > 0) {
    formData.append('filter', JSON.stringify(searchFilters));
  }
  
  formData.append('page', page.toString());
  formData.append('start', ((page - 1) * limit).toString());
  formData.append('limit', limit.toString());
  formData.append('sort', 'Kod');
  formData.append('dir', 'ASC');

  try {
    const response = await api.post<SparePartResponse>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Arama hatası:', error);
    throw error;
  }
};

/**
 * Yedek parçayı sepete ekler
 * @param sparePartId Yedek parça ID'si
 * @returns Sepete ekleme yanıtı
 */
export const addToCart = async (sparePartId: number, quantity: number = 1): Promise<AddToCartResponse> => {
  const formData = new URLSearchParams();
  formData.append('store', 'Sepet');
  formData.append('limitless', 'true');
  formData.append('id', sparePartId.toString());
  formData.append('amount', quantity.toString());

  try {
    const response = await api.post<AddToCartResponse>('/json', formData);
    return response.data;
  } catch (error) {
    console.error('Sepete ekleme hatası:', error);
    throw error;
  }
}; 

/**
 * Sepet öğesini siler
 * @param cartItem Sepet öğesi
 * @returns Silme yanıtı
 */
export const removeFromCart = async (cartItem: any): Promise<any> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'Sepet');
  formData.append('fields', 'Siparis,SiparisId,SiparisNo,YedekParca,Kod,Ad,GrupAdi,Gorsel,FiiliStok,Miktar,Fiyat,Indirim,TeminEdilen,Kalan,SiparisTutari,FaturaTutari,FiiliStokDurumu,PromosyonluMu,ModernArayuzdenMi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('prefilter', '[{"type":"int","field":"SiparisId","value":0}]');
  
  // Silme verisi
  const deleteData = {
    Siparis: cartItem.Siparis,
    YedekParca: cartItem.YedekParca,
    Miktar: cartItem.Miktar,
    Fiyat: cartItem.Fiyat,
    Indirim: cartItem.Indirim || 0,
    TeminEdilen: cartItem.TeminEdilen || 0,
    ModernArayuzdenMi: cartItem.ModernArayuzdenMi || false,
    Id: cartItem.Id,
    Tracker: ""
  };
  
  formData.append('data', JSON.stringify(deleteData));

  try {
    const response = await api.post('/data/destroy', formData);
    return response.data;
  } catch (error) {
    console.error('Sepetten silme hatası:', error);
    throw error;
  }
};

/**
 * Sepet öğesinin miktarını günceller
 * @param cartItem Sepet öğesi
 * @param quantity Yeni miktar
 * @returns Güncelleme yanıtı
 */
export const updateCartItemQuantity = async (cartItem: any, quantity: number): Promise<any> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'Sepet');
  formData.append('fields', 'Siparis,SiparisId,SiparisNo,YedekParca,Kod,Ad,GrupAdi,Gorsel,FiiliStok,Miktar,Fiyat,Indirim,TeminEdilen,Kalan,SiparisTutari,FaturaTutari,FiiliStokDurumu,PromosyonluMu,ModernArayuzdenMi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('prefilter', '[{"type":"int","field":"SiparisId","value":0}]');
  
  // Güncelleme verisi
  const updateData = {
    Siparis: cartItem.Siparis,
    YedekParca: cartItem.YedekParca,
    Miktar: quantity,
    Fiyat: cartItem.Fiyat,
    Indirim: cartItem.Indirim || 0,
    TeminEdilen: cartItem.TeminEdilen || 0,
    ModernArayuzdenMi: cartItem.ModernArayuzdenMi || false,
    Id: cartItem.Id,
    Tracker: ""
  };
  
  formData.append('data', JSON.stringify(updateData));

  try {
    const response = await api.post('/data/update', formData);
    return response.data;
  } catch (error) {
    console.error('Sepet miktarı güncelleme hatası:', error);
    throw error;
  }
};

/**
 * Kullanıcının adres bilgilerini getirir
 * @param userId Kullanıcı ID'si
 * @returns Kullanıcı adres bilgileri
 */
export const getUserAddress = async (userId: number): Promise<any> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'Cariler');
  formData.append('fields', 'KullaniciAdi,Ad,Il,Ilce,Ulke,IlAdi,IlceAdi,UlkeAdi,Adres,PostaKodu,Telefon1,Telefon2,Faks,EPosta,WebSitesi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', 'Id');
  formData.append('masterid', userId.toString());
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '50');
  formData.append('sort', 'Id');
  formData.append('dir', 'ASC');

  try {
    const response = await api.post('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Kullanıcı adres bilgisi alma hatası:', error);
    throw error;
  }
};

/**
 * Ülke listesini getirir
 * @returns Ülke listesi
 */
export const getCountries = async (): Promise<any> => {
  const formData = new URLSearchParams();
  formData.append('store', 'Ulkeler');
  formData.append('limitless', 'true');
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '25');

  try {
    const response = await api.post('/json', formData);
    return response.data;
  } catch (error) {
    console.error('Ülke listesi alma hatası:', error);
    throw error;
  }
};

/**
 * Seçili ülkeye ait il listesini getirir
 * @param countryId Ülke ID'si
 * @returns İl listesi
 */
export const getCities = async (countryId: number): Promise<any> => {
  const formData = new URLSearchParams();
  formData.append('store', 'Iller');
  formData.append('ulke', countryId.toString());
  formData.append('limitless', 'true');
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '25');

  try {
    const response = await api.post('/json', formData);
    return response.data;
  } catch (error) {
    console.error('İl listesi alma hatası:', error);
    throw error;
  }
};

/**
 * Seçili ile ait ilçe listesini getirir
 * @param cityId İl ID'si
 * @returns İlçe listesi
 */
export const getDistricts = async (cityId: number): Promise<any> => {
  const formData = new URLSearchParams();
  formData.append('store', 'Ilceler');
  formData.append('il', cityId.toString());
  formData.append('limitless', 'true');
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '25');

  try {
    const response = await api.post('/json', formData);
    return response.data;
  } catch (error) {
    console.error('İlçe listesi alma hatası:', error);
    throw error;
  }
};

/**
 * Taşıyıcı listesini getirir
 * @returns Taşıyıcı listesi
 */
export const getCarriers = async (): Promise<any> => {
  const formData = new URLSearchParams();
  formData.append('store', 'Tasiyicilar');
  formData.append('limitless', 'true');
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '25');

  try {
    const response = await api.post('/json', formData);
    return response.data;
  } catch (error) {
    console.error('Taşıyıcı listesi alma hatası:', error);
    throw error;
  }
};

/**
 * Sipariş oluşturur
 * @param orderData Sipariş bilgileri
 * @returns Sipariş yanıtı (Id ve No)
 */
export const createOrder = async (orderData: {
  address: string;
  country: any;
  city: any;
  district: any;
  phone: string;
  orderNotes: string;
  carrier?: any;
}): Promise<{ data: [{ Id: number; No: string }]; total: number }> => {
  const formData = new URLSearchParams();
  formData.append('store', 'Siparis');
  formData.append('model', 'Siparisler');
  formData.append('limitless', 'true');
  
  const data: any = {
    Il: orderData.city.Id.toString(),
    Ilce: orderData.district.Id.toString(),
    Ulke: orderData.country.Id.toString(),
    Adres: orderData.address,
    PostaKodu: "",
    Telefon1: orderData.phone,
    Telefon2: "",
    ServisNotlari: orderData.orderNotes
  };

  // Taşıyıcı seçildiyse ekle
  if (orderData.carrier) {
    data.Tasiyici = orderData.carrier.Id.toString();
  }
  
  formData.append('data', JSON.stringify(data));

  try {
    const response = await api.post('/json', formData);
    return response.data;
  } catch (error) {
    console.error('Sipariş oluşturma hatası:', error);
    throw error;
  }
};

/**
 * Sepet öğesini siparişe bağlar
 * @param cartItem Sepet öğesi
 * @param orderId Sipariş ID'si
 * @returns Güncelleme yanıtı
 */
export const linkCartItemToOrder = async (cartItem: any, orderId: number): Promise<any> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'Sepet');
  formData.append('fields', 'Siparis,SiparisId,SiparisNo,YedekParca,Kod,Ad,GrupAdi,Gorsel,FiiliStok,Miktar,Fiyat,Indirim,TeminEdilen,Kalan,SiparisTutari,FaturaTutari,FiiliStokDurumu,PromosyonluMu,ModernArayuzdenMi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('prefilter', '[{"type":"int","field":"SiparisId","value":0}]');
  
  // Güncelleme verisi
  const updateData = {
    Siparis: orderId,
    YedekParca: cartItem.YedekParca,
    Miktar: cartItem.Miktar,
    Fiyat: cartItem.Fiyat,
    Indirim: cartItem.Indirim || 0,
    TeminEdilen: cartItem.TeminEdilen || 0,
    ModernArayuzdenMi: false,
    Id: cartItem.Id,
    Tracker: ""
  };
  
  formData.append('data', JSON.stringify(updateData));

  try {
    const response = await api.post('/data/update', formData);
    return response.data;
  } catch (error) {
    console.error('Sepet öğesini siparişe bağlama hatası:', error);
    throw error;
  }
};

/**
 * Sipariş detaylarını getirir
 * @param orderId Sipariş ID'si
 * @returns Sipariş detayları
 */
export const getOrderDetails = async (orderId: number): Promise<any> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'true');
  formData.append('storeId', storeId);
  formData.append('model', 'Sepet');
  formData.append('fields', 'Siparis,SiparisId,SiparisNo,YedekParca,Kod,Ad,GrupAdi,Gorsel,FiiliStok,Miktar,Fiyat,Indirim,TeminEdilen,Kalan,SiparisTutari,FaturaTutari,FiiliStokDurumu,PromosyonluMu,ModernArayuzdenMi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', 'Siparis');
  formData.append('masterid', orderId.toString());
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '50');
  formData.append('sort', 'Id');
  formData.append('dir', 'ASC');

  try {
    const response = await api.post('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Sipariş detayları alma hatası:', error);
    throw error;
  }
};

/**
 * Sepeti temizler (tüm öğeleri siler)
 * @returns Temizleme yanıtı
 */
export const clearCart = async (): Promise<any> => {
  try {
    // Önce sepet öğelerini al
    const cartResponse = await getCartItems();
    
    if (cartResponse.total === 0) {
      return { success: true, message: 'Sepet zaten boş' };
    }

    // Tüm öğeleri tek tek sil (destroy endpoint'i için)
    const deletePromises = cartResponse.data.map(async (item) => {
      // Benzersiz bir storeId oluştur
      const uniqueId = Date.now() + Math.random();
      const storeId = `store-${uniqueId}`;
      
      const formData = new URLSearchParams();
      formData.append('isTree', 'false');
      formData.append('limitless', 'false');
      formData.append('storeId', storeId);
      formData.append('model', 'Sepet');
      formData.append('fields', 'Siparis,SiparisId,SiparisNo,YedekParca,Kod,Ad,GrupAdi,Gorsel,FiiliStok,Miktar,Fiyat,Indirim,TeminEdilen,Kalan,SiparisTutari,FaturaTutari,FiiliStokDurumu,PromosyonluMu,ModernArayuzdenMi,Id');
      formData.append('writemodel', '');
      formData.append('fromview', 'true');
      formData.append('masterfield', '');
      formData.append('masterid', '0');
      formData.append('prefilter', '[{"type":"int","field":"SiparisId","value":0}]');
      
      const deleteData = {
        Siparis: item.Siparis,
        YedekParca: item.YedekParca,
        Miktar: item.Miktar,
        Fiyat: item.Fiyat,
        Indirim: item.Indirim || 0,
        TeminEdilen: item.TeminEdilen || 0,
        ModernArayuzdenMi: item.ModernArayuzdenMi || false,
        Id: item.Id,
        Tracker: ""
      };
      
      formData.append('data', JSON.stringify(deleteData));
      
      return api.post('/data/destroy', formData);
    });

    // Tüm silme işlemlerini bekle
    await Promise.all(deletePromises);
    
    return { success: true, message: 'Sepet temizlendi' };
  } catch (error) {
    console.error('Sepet temizleme hatası:', error);
    throw error;
  }
};

/**
 * Sipariş bilgilerini getirir (adres, notlar, durum vb.)
 * @param orderNo Sipariş numarası
 * @returns Sipariş bilgileri
 */
export const getOrderInfo = async (orderNo: string): Promise<any> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'Siparisler');
  formData.append('fields', 'No,ModernArayuzdenMi,Talep,TaleptenMi,TalepNo,Bakim,BakimdanMi,BakimNo,Miktar,TeminEdilen,Kalan,SiparisTutari,FaturaTutari,Il,Ilce,Ulke,IlAdi,IlceAdi,UlkeAdi,Adres,PostaKodu,Telefon1,Telefon2,Tasiyici,TasiyiciAdi,SonIslem,IslemAdi,SonEtkinlik,Notlar,ServisNotlari,Tarih,EtkinlikTarihi,IslemTarihi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('filter', `[{"type":"string","value":"${orderNo}","field":"No"}]`);
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '50');
  formData.append('sort', 'No');
  formData.append('dir', 'DESC');

  try {
    const response = await api.post('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Sipariş bilgileri alma hatası:', error);
    throw error;
  }
};

/**
 * Sipariş silme
 * @param orderId Silinecek siparişin ID'si
 * @returns Silme işlemi sonucu
 */
export const deleteOrder = async (orderId: number): Promise<{ success: boolean }> => {
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', `store-${Date.now()}`);
  formData.append('model', 'Siparisler');
  formData.append('fields', 'No,ModernArayuzdenMi,Talep,TaleptenMi,TalepNo,Bakim,BakimdanMi,BakimNo,Miktar,TeminEdilen,Kalan,SiparisTutari,FaturaTutari,Il,Ilce,Ulke,IlAdi,IlceAdi,UlkeAdi,Adres,PostaKodu,Telefon1,Telefon2,SonIslem,IslemAdi,SonEtkinlik,Notlar,ServisNotlari,Tarih,EtkinlikTarihi,IslemTarihi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('data', JSON.stringify({ Id: orderId, Tracker: "" }));

  try {
    const response = await api.post('/data/destroy', formData);
    return response.data;
  } catch (error) {
    console.error('Sipariş silme hatası:', error);
    throw error;
  }
};

/**
 * Kullanıcının siparişlerini listeler
 * @param page Sayfa numarası
 * @param limit Sayfa başına kayıt sayısı
 * @param statusFilter Durum filtresi (opsiyonel)
 * @param dateFilter Tarih filtresi (opsiyonel) - {startDate?: string, endDate?: string}
 * @returns Sipariş listesi
 */
export const getOrders = async (
  page: number = 1, 
  limit: number = 50, 
  statusFilter?: string[] | any[], 
  dateFilter?: {startDate?: string, endDate?: string}
): Promise<any> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'Siparisler');
  formData.append('fields', 'No,Cari,CariAdi,Talep,TaleptenMi,TalepNo,Bakim,BakimdanMi,BakimNo,CaridenMi,ModernArayuzdenMi,Miktar,TeminEdilen,Kalan,SiparisTutari,FaturaTutari,Il,Ilce,Ulke,IlAdi,IlceAdi,UlkeAdi,Adres,PostaKodu,Telefon1,Telefon2,SonIslem,IslemAdi,SonEtkinlik,ServisNotlari,Tarih,EtkinlikTarihi,IslemTarihi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  
  // Filtreleri oluştur
  const filters: any[] = [];
  
  // Durum filtresi varsa ekle
  if (statusFilter && statusFilter.length > 0) {
    // Eğer statusFilter array'inin ilk elemanı obje ise, direkt filtre olarak kullan
    if (typeof statusFilter[0] === 'object' && statusFilter[0] !== null) {
      filters.push(...statusFilter);
    } else {
      // String array ise eski mantığı kullan
      filters.push({
        "type": "list",
        "value": statusFilter,
        "field": "IslemAdi"
      });
    }
  }
  
  // Tarih filtresi varsa ekle
  if (dateFilter) {
    if (dateFilter.startDate) {
      filters.push({
        "type": "date",
        "comparison": "gt",
        "value": dateFilter.startDate,
        "field": "Tarih"
      });
    }
    
    if (dateFilter.endDate) {
      filters.push({
        "type": "date",
        "comparison": "lt",
        "value": dateFilter.endDate,
        "field": "Tarih"
      });
    }
  }
  
  // Filtreler varsa ekle
  if (filters.length > 0) {
    formData.append('filter', JSON.stringify(filters));
  }
  
  formData.append('page', page.toString());
  formData.append('start', ((page - 1) * limit).toString());
  formData.append('limit', limit.toString());
  formData.append('sort', 'No');
  formData.append('dir', 'DESC');

  try {
    const response = await api.post<any>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Sipariş listesi alma hatası:', error);
    throw error;
  }
}; 